import { useEffect, useState, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { getCampaigns } from '../api/campaigns'
import apiClient from '../api/client'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { ErrorMessage } from '../components/ErrorMessage'
import { useAuth } from '../context/AuthContext'
import type { Campaign } from '../types'
import styles from './CampaignsPage.module.css'

function fmt(n: number, d = 0) {
  return n.toLocaleString(undefined, { maximumFractionDigits: d })
}

function fmtUsd(n: number) {
  return '₹' + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function fmtPct(n: number) {
  return (n * 100).toFixed(2) + '%'
}

function ScorePill({ score }: { score: number }) {
  const color = score >= 80 ? '#059669' : score >= 60 ? '#d97706' : '#dc2626'
  const bg = score >= 80 ? '#ecfdf5' : score >= 60 ? '#fffbeb' : '#fef2f2'
  const border = score >= 80 ? '#a7f3d0' : score >= 60 ? '#fcd34d' : '#fca5a5'
  return (
    <span style={{
      background: bg, color, border: `1px solid ${border}`,
      borderRadius: '999px', padding: '0.2rem 0.65rem',
      fontSize: '0.75rem', fontWeight: 700,
    }}>
      {score}/100
    </span>
  )
}

function MetricCell({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.metric}>
      <span className={styles.metricLabel}>{label}</span>
      <span className={styles.metricValue}>{value}</span>
    </div>
  )
}

export function CampaignsPage() {
  const { canWrite } = useAuth()
  const navigate = useNavigate()
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<'all' | 'draft' | 'active'>('all')
  const [editingCampaign, setEditingCampaign] = useState<any | null>(null)
  const [editSaving, setEditSaving] = useState(false)
  const [editUploading, setEditUploading] = useState(false)
  const [editMsg, setEditMsg] = useState<string | null>(null)
  const [editErr, setEditErr] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const load = async () => {
    setLoading(true); setError(null)
    try { setCampaigns(await getCampaigns()) }
    catch { setError('Failed to load campaigns.') }
    finally { setLoading(false) }
  }

  useEffect(() => { load() }, [])

  function openEdit(c: any) {
    setEditingCampaign({ ...c })
    setEditMsg(null)
    setEditErr(null)
  }

  function closeEdit() {
    setEditingCampaign(null)
    setEditMsg(null)
    setEditErr(null)
  }

  async function handleEditImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      setEditUploading(true)
      setEditErr(null)
      const presignRes = await apiClient.post('/get-upload-url', {
        filename: file.name,
        content_type: file.type || 'image/jpeg',
      })
      const { upload_url, image_url } = presignRes.data
      const s3Res = await fetch(upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'image/jpeg' },
        body: file,
      })
      if (!s3Res.ok) throw new Error('S3 upload failed')
      setEditingCampaign((prev: any) => ({ ...prev, image_url, _imageFileName: file.name }))
      setEditMsg('Image uploaded')
    } catch {
      setEditErr('Image upload failed. Try again.')
    } finally {
      setEditUploading(false)
      e.target.value = ''
    }
  }

  async function saveEdit() {
    if (!editingCampaign) return
    const id = editingCampaign.id || editingCampaign.campaign_id
    try {
      setEditSaving(true)
      setEditErr(null)
      const updated = await apiClient.put(`/campaigns/${id}`, {
        headline: editingCampaign.headline,
        primary_text: editingCampaign.primary_text,
        call_to_action: editingCampaign.call_to_action,
        audience: editingCampaign.audience,
        image_url: editingCampaign.image_url,
        link_url: editingCampaign.link_url,
        daily_budget: editingCampaign.daily_budget ? Number(editingCampaign.daily_budget) : undefined,
        scheduled_time: editingCampaign.scheduled_time || undefined,
        status: editingCampaign.status,
      })
      setCampaigns(prev => prev.map((c: any) =>
        (c.id || c.campaign_id) === id ? { ...c, ...updated.data } : c
      ))
      setEditMsg('Campaign updated successfully')
      setTimeout(closeEdit, 900)
    } catch {
      setEditErr('Failed to save. Please try again.')
    } finally {
      setEditSaving(false)
    }
  }

  if (loading) return <LoadingSpinner message="Loading campaigns…" />
  if (error) return <ErrorMessage message={error} onRetry={load} />

  const filtered = campaigns.filter(c => {
    const matchesSearch = !search ||
      c.campaign_name?.toLowerCase().includes(search.toLowerCase()) ||
      (c as any).product?.toLowerCase().includes(search.toLowerCase()) ||
      (c as any).headline?.toLowerCase().includes(search.toLowerCase())
    const matchesFilter = filter === 'all' || ((c as any).status || 'draft') === filter
    return matchesSearch && matchesFilter
  })

  const totalCampaigns = campaigns.length
  const activeCampaigns = campaigns.filter(c => (c as any).status === 'active').length
  const avgScore = campaigns.length
    ? Math.round(campaigns.reduce((s, c) => s + ((c as any).performance_score || 0), 0) / campaigns.length)
    : 0

  return (
    <div className={styles.page}>

      {/* Header */}
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.heading}>Ad Campaigns</h1>
          <p className={styles.subheading}>
            AI-generated campaigns ready to publish to Facebook
          </p>
        </div>
        {canWrite && (
          <button className={styles.createBtn} onClick={() => navigate('/dashboard/create-campaign')}>
            + Create Campaign
          </button>
        )}
      </div>

      {/* Summary Strip */}
      <div className={styles.summaryStrip}>
        <div className={styles.summaryItem}>
          <span className={styles.summaryValue}>{totalCampaigns}</span>
          <span className={styles.summaryLabel}>Total</span>
        </div>
        <div className={styles.summaryDivider} />
        <div className={styles.summaryItem}>
          <span className={styles.summaryValue} style={{ color: '#059669' }}>{activeCampaigns}</span>
          <span className={styles.summaryLabel}>Active</span>
        </div>
        <div className={styles.summaryDivider} />
        <div className={styles.summaryItem}>
          <span className={styles.summaryValue} style={{ color: '#1d4ed8' }}>{avgScore}</span>
          <span className={styles.summaryLabel}>Avg AI Score</span>
        </div>
        <div className={styles.summaryDivider} />
        <div className={styles.summaryItem}>
          <span className={styles.summaryValue}>{totalCampaigns - activeCampaigns}</span>
          <span className={styles.summaryLabel}>Draft</span>
        </div>
      </div>

      {/* Filter Bar */}
      <div className={styles.filterBar}>
        <input
          className={styles.searchInput}
          placeholder="Search campaigns…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div className={styles.filterTabs}>
          {(['all', 'draft', 'active'] as const).map(f => (
            <button
              key={f}
              className={`${styles.filterTab} ${filter === f ? styles.filterTabActive : ''}`}
              onClick={() => setFilter(f)}
            >
              {f === 'all' ? 'All' : f === 'active' ? 'Active' : 'Draft'}
            </button>
          ))}
        </div>
      </div>

      {/* Empty */}
      {filtered.length === 0 ? (
        <div className={styles.empty}>
          <span className={styles.emptyIcon}>🚀</span>
          <h3 className={styles.emptyTitle}>
            {campaigns.length === 0 ? 'No campaigns yet' : 'No matching campaigns'}
          </h3>
          <p className={styles.emptyText}>
            {campaigns.length === 0
              ? 'Go to Create to generate your first AI-powered Facebook ad campaign.'
              : 'Try a different search or filter.'}
          </p>
          {campaigns.length === 0 && canWrite && (
            <button className={styles.createBtn} onClick={() => navigate('/dashboard/create-campaign')}>
              + Create your first campaign
            </button>
          )}
        </div>
      ) : (
        <div className={styles.grid}>
          {filtered.map((c: any) => {
            const m = c?.latest_metrics || {}
            const hasMetrics = m?.impressions || m?.clicks || m?.spend
            return (
              <div key={c.campaign_id || c.id} className={styles.card}>

                {/* Card Header */}
                <div className={styles.cardTop}>
                  <div className={styles.cardTitleRow}>
                    <h2 className={styles.cardTitle}>
                      {c.campaign_name || c.headline || c.product || 'Untitled Campaign'}
                    </h2>
                    <div className={styles.cardBadges}>
                      {c.performance_score > 0 && <ScorePill score={c.performance_score} />}
                      <span className={`${styles.statusBadge} ${(c.status || 'draft') === 'active' ? styles.statusActive : styles.statusDraft}`}>
                        {c.status || 'draft'}
                      </span>
                    </div>
                  </div>

                  {c.platform && (
                    <span className={styles.platformTag}>
                      {c.platform === 'Facebook & Instagram' ? '📘 Meta Ads' : `📢 ${c.platform}`}
                    </span>
                  )}
                </div>

                {/* Ad Copy Preview */}
                {(c.headline || c.primary_text) && (
                  <div className={styles.copyPreview}>
                    {c.headline && <p className={styles.copyHeadline}>{c.headline}</p>}
                    {c.primary_text && (
                      <p className={styles.copyText}>
                        {c.primary_text.length > 100 ? c.primary_text.slice(0, 100) + '…' : c.primary_text}
                      </p>
                    )}
                    {c.call_to_action && (
                      <span className={styles.ctaTag}>{c.call_to_action}</span>
                    )}
                  </div>
                )}

                {/* Metrics */}
                {hasMetrics ? (
                  <div className={styles.metrics}>
                    <MetricCell label="Impressions" value={fmt(m.impressions || 0)} />
                    <MetricCell label="Clicks" value={fmt(m.clicks || 0)} />
                    <MetricCell label="Spend" value={fmtUsd(m.spend || 0)} />
                    <MetricCell label="CTR" value={fmtPct(m.ctr || 0)} />
                    <MetricCell label="CPC" value={fmtUsd(m.cpc || 0)} />
                    <MetricCell label="ROAS" value={fmt(m.roas || 0, 2) + 'x'} />
                  </div>
                ) : (
                  <div className={styles.noMetrics}>
                    <span>📊</span>
                    <span>Metrics available after publishing</span>
                  </div>
                )}

                {/* Footer Actions */}
                <div className={styles.cardFooter}>
                  {c.budget && (
                    <span className={styles.budgetTag}>💰 {c.budget}</span>
                  )}
                  <div className={styles.cardActions}>
                    <Link
                      to={`/dashboard/campaigns/${c.campaign_id || c.id}`}
                      className={styles.actionLink}
                    >
                      Analytics →
                    </Link>
                    {canWrite && (
                      <>
                        <button
                          className={styles.editBtn}
                          onClick={() => openEdit(c)}
                        >
                          Edit
                        </button>
                        <button
                          className={styles.publishBtn}
                          onClick={() => navigate('/dashboard/create-campaign')}
                        >
                          Publish
                        </button>
                      </>
                    )}
                  </div>
                </div>

              </div>
            )
          })}
        </div>
      )}

      {/* ── Edit Campaign Modal ── */}
      {editingCampaign && (
        <div className={styles.modalOverlay} onClick={closeEdit}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>Edit Campaign</h2>
              <button className={styles.modalClose} onClick={closeEdit}>✕</button>
            </div>

            <div className={styles.modalBody}>
              {editMsg && <div className={styles.editSuccess}>{editMsg}</div>}
              {editErr && <div className={styles.editError}>{editErr}</div>}

              <div className={styles.editField}>
                <label className={styles.editLabel}>Headline</label>
                <input className={styles.editInput} value={editingCampaign.headline || ''}
                  onChange={e => setEditingCampaign((p: any) => ({ ...p, headline: e.target.value }))} />
              </div>

              <div className={styles.editField}>
                <label className={styles.editLabel}>Primary Text</label>
                <textarea className={styles.editTextarea} rows={3} value={editingCampaign.primary_text || ''}
                  onChange={e => setEditingCampaign((p: any) => ({ ...p, primary_text: e.target.value }))} />
              </div>

              <div className={styles.editRow}>
                <div className={styles.editField}>
                  <label className={styles.editLabel}>Call to Action</label>
                  <input className={styles.editInput} value={editingCampaign.call_to_action || ''}
                    onChange={e => setEditingCampaign((p: any) => ({ ...p, call_to_action: e.target.value }))} />
                </div>
                <div className={styles.editField}>
                  <label className={styles.editLabel}>Status</label>
                  <select className={styles.editInput} value={editingCampaign.status || 'draft'}
                    onChange={e => setEditingCampaign((p: any) => ({ ...p, status: e.target.value }))}>
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                  </select>
                </div>
              </div>

              <div className={styles.editField}>
                <label className={styles.editLabel}>Target Audience</label>
                <input className={styles.editInput} value={editingCampaign.audience || ''}
                  onChange={e => setEditingCampaign((p: any) => ({ ...p, audience: e.target.value }))} />
              </div>

              <div className={styles.editRow}>
                <div className={styles.editField}>
                  <label className={styles.editLabel}>Destination URL</label>
                  <input className={styles.editInput} value={editingCampaign.link_url || ''}
                    onChange={e => setEditingCampaign((p: any) => ({ ...p, link_url: e.target.value }))} />
                </div>
                <div className={styles.editField}>
                  <label className={styles.editLabel}>Daily Budget (₹)</label>
                  <input className={styles.editInput} type="number" value={editingCampaign.daily_budget || ''}
                    onChange={e => setEditingCampaign((p: any) => ({ ...p, daily_budget: e.target.value }))} />
                </div>
              </div>

              {/* Poster Image */}
              <div className={styles.editField}>
                <label className={styles.editLabel}>Poster Image</label>
                <div className={styles.editPosterRow}>
                  <label className={styles.editUploadBtn}>
                    {editUploading ? 'Uploading...' : editingCampaign._imageFileName ? `📎 ${editingCampaign._imageFileName}` : '📁 Upload New Image'}
                    <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp"
                      style={{ display: 'none' }} onChange={handleEditImageUpload} disabled={editUploading} />
                  </label>
                  <button className={styles.editPosterStudioBtn}
                    onClick={() => window.open('https://poster.digitransolutions.in/', '_blank')}>
                    Open Poster Studio
                  </button>
                </div>
                {editingCampaign.image_url && (
                  <div className={styles.editPosterPreviewWrap}>
                    <img src={editingCampaign.image_url} alt="Poster preview" className={styles.editPosterPreview}
                      onError={e => { (e.target as HTMLImageElement).style.display = 'none' }} />
                    <button className={styles.editRemoveImage}
                      onClick={() => setEditingCampaign((p: any) => ({ ...p, image_url: '', _imageFileName: null }))}>
                      Remove image
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button className={styles.modalCancel} onClick={closeEdit}>Cancel</button>
              <button className={styles.modalSave} onClick={saveEdit} disabled={editSaving}>
                {editSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
